import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import './modalcursos.css';
import carga from '../../carga/carga';
import { obtenerProgramaPorCodigo } from '../../../funtions';

function ModalCursos({ isOpen, courseId, onClose }) {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && courseId) {
      loadCourseDetails();
    }
  }, [isOpen, courseId]);

  const loadCourseDetails = async () => {
    try {
      setLoading(true);
      carga.mostrarCarga();
      const courseData = await obtenerProgramaPorCodigo(courseId);
      setSelectedCourse(courseData);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Error cargando detalles del curso:', error);
    } finally {
      setLoading(false);
      carga.ocultarCarga();
    }
  };

  const renderFormattedText = (text) => {
    if (!text) return null;

    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== '');
    const blocks = [];
    let currentBlock = null;

    lines.forEach((line) => {
      if (/^#title\b/i.test(line)) {
        const titleText = line.replace(/^#title\s*/i, '').trim();
        currentBlock = { title: titleText, items: [] };
        blocks.push(currentBlock);
      } else if (currentBlock) {
        currentBlock.items.push(line);
      } else {
        blocks.push({ title: null, text: line });
      }
    });

    return blocks.map((block, index) => {
      if (block.title !== null) {
        return (
          <div className="formatted-section" key={index}>
            <p className="formatted-title">{block.title}</p>
            {block.items.length > 0 && (
              <ul className="formatted-check-list">
                {block.items.map((item, idx) => (
                  <li key={idx} className="formatted-check-item">
                    <FaCheck className="formatted-check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }

      return (
        <p key={index} className="formatted-paragraph" style={{ whiteSpace: 'pre-line' }}>
          {block.text}
        </p>
      );
    });
  };

  const nextImage = () => {
    if (selectedCourse?.cards) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedCourse.cards.length);
    }
  };

  const prevImage = () => {
    if (selectedCourse?.cards) {
      setCurrentImageIndex((prev) =>
        (prev - 1 + selectedCourse.cards.length) % selectedCourse.cards.length
      );
    }
  };

  const handleClose = () => {
    setSelectedCourse(null);
    setCurrentImageIndex(0);
    onClose();
  };

  if (!isOpen) return null;

  const totalImages = selectedCourse?.cards?.length ?? 0;

  return (
    <div className="curso-modal-overlay" onClick={handleClose}>
      <div
        className="curso-modal-content-large"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón cerrar */}
        <button className="curso-modal-close" onClick={handleClose}>×</button>

        {/* ===== ESTADOS ===== */}
        {loading && (
          <div className="modal-loading">
            <div className="modal-loading-spinner" />
            <p>Cargando programa...</p>
          </div>
        )}

        {!loading && !selectedCourse && (
          <div className="modal-error">
            <p>No se pudo cargar la información del programa</p>
          </div>
        )}

        {/* ===== CONTENIDO PRINCIPAL ===== */}
        {!loading && selectedCourse && (
          <div className="curso-modal-layout">

            {/* ─── COLUMNA IZQUIERDA: Slider ─── */}
            <div className="curso-modal-slider">
              <div className="curso-slider-window">
                <div
                  className="curso-slider-track"
                  style={{
                    transform: `translateX(-${currentImageIndex * 100}%)`,
                    transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  }}
                >
                  {selectedCourse.cards?.map((cardUrl, idx) => (
                    <div key={idx} className="curso-slider-item">
                      <img
                        src={cardUrl}
                        alt={`${selectedCourse.nombre} — ${idx + 1}`}
                        className="curso-modal-image"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>

                {/* Controles (solo si hay más de 1 imagen) */}
                {totalImages > 1 && (
                  <>
                    <button
                      className="slider-control prev"
                      onClick={prevImage}
                      aria-label="Imagen anterior"
                    >‹</button>
                    <button
                      className="slider-control next"
                      onClick={nextImage}
                      aria-label="Imagen siguiente"
                    >›</button>

                    {/* Indicadores barra */}
                    <div className="slider-indicators">
                      {selectedCourse.cards.map((_, idx) => (
                        <button
                          key={idx}
                          className={`indicator ${idx === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(idx)}
                          aria-label={`Ir a imagen ${idx + 1}`}
                        />
                      ))}
                    </div>

                    {/* Contador numérico */}
                    <div className="slider-counter">
                      <strong>{currentImageIndex + 1}</strong> / {totalImages}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ─── COLUMNA DERECHA: Info ─── */}
            <div className="curso-modal-info">

              {/* Header */}
              <div className="curso-modal-header">
                <div className="curso-modal-category-row">
                  <span className="category-line" />
                  <p className="curso-modal-category">{selectedCourse.categoria}</p>
                </div>

                <h2>{selectedCourse.nombre}</h2>

                {selectedCourse.codigo && (
                  <div className="curso-modal-code">
                    <span>Código</span>
                    <strong>{selectedCourse.codigo}</strong>
                  </div>
                )}
              </div>

              <div className="header-divider" />

              {/* Cuerpo scrollable */}
              <div className="curso-modal-body-content">

                {/* Descripción */}
                {selectedCourse.descripcion && (
                  <div className="curso-modal-description">
                    <h3>Descripción</h3>
                    <p>{selectedCourse.descripcion}</p>
                  </div>
                )}

                {/* Plan de estudios */}
                {selectedCourse.pensum && (
                  <div className="curso-modal-pensum">
                    <h3>Plan de Estudios</h3>
                    <div className="formatted-text">
                      {renderFormattedText(selectedCourse.pensum)}
                    </div>
                  </div>
                )}

                {/* Perfil del egresado */}
                {selectedCourse.perfil_egresado && (
                  <div className="curso-modal-profile">
                    <h3>Perfil del Egresado</h3>
                    <div className="formatted-text">
                      {renderFormattedText(selectedCourse.perfil_egresado)}
                    </div>
                  </div>
                )}

                {/* Campo laboral */}
                {selectedCourse.campo_laboral && (
                  <div className="curso-modal-field">
                    <h3>Campo Laboral</h3>
                    <div className="formatted-text">
                      {renderFormattedText(selectedCourse.campo_laboral)}
                    </div>
                  </div>
                )}

                {/* Requisitos */}
                {selectedCourse.requisitos?.length > 0 && (
                  <div className="curso-modal-requisitos">
                    <h3>Requisitos</h3>
                    <ul>
                      {selectedCourse.requisitos.map((requisito, idx) => (
                        <li key={idx}>{requisito}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Modalidades */}
                {selectedCourse.modalidades?.length > 0 && (
                  <div className="curso-modal-modalidades">
                    <h3>Modalidades</h3>
                    <ul>
                      {selectedCourse.modalidades.map((modalidad, idx) => (
                        <li key={idx}>{modalidad}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Materias clave */}
                {selectedCourse.materias_claves?.length > 0 && (
                  <div className="curso-modal-materias">
                    <h3>Plan de estudios</h3>
                    <ul>
                      {selectedCourse.materias_claves.map((materia, idx) => (
                        <li key={idx}>{materia}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Habilidades */}
                {selectedCourse.habilidades?.length > 0 && (
                  <div className="curso-modal-skills">
                    <h3>Habilidades</h3>
                    <ul>
                      {selectedCourse.habilidades.map((habilidad, idx) => (
                        <li key={idx}>{habilidad}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Detalles adicionales (si los hay en el objeto) */}
                {(selectedCourse.duracion || selectedCourse.modalidad ||
                  selectedCourse.creditos || selectedCourse.nivel) && (
                  <div className="curso-modal-details">
                    {selectedCourse.duracion && (
                      <div className="detail-item">
                        <span className="detail-label">Duración</span>
                        <span className="detail-value">{selectedCourse.duracion}</span>
                      </div>
                    )}
                    {selectedCourse.modalidad && (
                      <div className="detail-item">
                        <span className="detail-label">Modalidad</span>
                        <span className="detail-value">{selectedCourse.modalidad}</span>
                      </div>
                    )}
                    {selectedCourse.creditos && (
                      <div className="detail-item">
                        <span className="detail-label">Créditos</span>
                        <span className="detail-value">{selectedCourse.creditos}</span>
                      </div>
                    )}
                    {selectedCourse.nivel && (
                      <div className="detail-item">
                        <span className="detail-label">Nivel</span>
                        <span className="detail-value">{selectedCourse.nivel}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="footer-divider" />

              {/* CTA */}
              <div className="curso-modal-actions">
                <button
                  className="curso-modal-button"
                  onClick={() => { navigate('/inscribete'); handleClose(); }}
                >
                  Inscribirse ahora
                </button>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

export default ModalCursos;